/** Shared search-param shapes + validators for the mail routes. */

export interface MailSearch {
  /** Active IMAP/SMTP account id. */
  acct?: string;
  /** Active folder (default INBOX). */
  folder?: string;
  /** Selected message uid (opens the reader). */
  uid?: number;
}

export type ComposeMode = "new" | "reply" | "reply-all" | "forward";

export interface ComposeSearch {
  acct?: string;
  folder?: string;
  /** Original message uid to quote/reply from. */
  uid?: number;
  mode?: ComposeMode;
}

export interface MailSearchQuery {
  acct?: string;
  q?: string;
}

function str(v: unknown): string | undefined {
  return typeof v === "string" && v.trim() !== "" ? v : undefined;
}

function num(v: unknown): number | undefined {
  if (typeof v === "number" && Number.isInteger(v)) {
    return v;
  }
  if (typeof v === "string" && /^\d+$/.test(v)) {
    return Number.parseInt(v, 10);
  }
  return undefined;
}

export function validateMailSearch(search: Record<string, unknown>): MailSearch {
  return { acct: str(search.acct), folder: str(search.folder), uid: num(search.uid) };
}

const COMPOSE_MODES: ComposeMode[] = ["new", "reply", "reply-all", "forward"];

export function validateComposeSearch(search: Record<string, unknown>): ComposeSearch {
  const raw = str(search.mode);
  return {
    acct: str(search.acct),
    folder: str(search.folder),
    uid: num(search.uid),
    // `find` narrows to `ComposeMode | undefined` without an unsafe assertion.
    mode: COMPOSE_MODES.find((m) => m === raw),
  };
}

export function validateMailSearchQuery(search: Record<string, unknown>): MailSearchQuery {
  return { acct: str(search.acct), q: str(search.q) };
}

/** Outcome of the OAuth connect round-trip, set by the server's 302 back. */
export interface MailSettingsSearch {
  /** `connected` | `error` (anything else is dropped). */
  oauth?: "connected" | "error";
  /** The mailbox address that was connected (on success). */
  email?: string;
  /** Machine-readable failure reason (on error), e.g. `access_denied`. */
  reason?: string;
}

export function validateMailSettingsSearch(search: Record<string, unknown>): MailSettingsSearch {
  const raw = str(search.oauth);
  return {
    oauth: raw === "connected" || raw === "error" ? raw : undefined,
    email: str(search.email),
    reason: str(search.reason),
  };
}
